// src/site/hooks/useSiteCatalog.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  documentId,
} from 'firebase/firestore';
import { db } from '../../services/firebase';

// 'sala-comercial' -> 'Sala Comercial'
const fromSlugTitle = (s = '') =>
  String(s)
    .split('-')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join(' ');

// normaliza filtros removendo chaves vazias e coerção básica
const normalizeFiltros = (f = {}) => {
  const out = { ...f };
  Object.keys(out).forEach((k) => {
    const v = out[k];
    if (v === '' || v == null) delete out[k];
  });
  return out;
};

const toNumber = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

// ===============================
// utils
// ===============================
const chunk = (arr = [], size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const looksHydrated = (obj = {}) => {
  if (obj.thumbnail) return true;
  if (Array.isArray(obj.imagens) && obj.imagens.length) return true;
  if (Array.isArray(obj.fotos) && obj.fotos.length) return true;
  if (Array.isArray(obj.images) && obj.images.length) return true;
  if (obj.capaUrl || obj.fotoDestaque || obj.capa) return true;
  return false;
};

// ✅ hidrata em paralelo (muito mais rápido)
async function hydrateFromImoveis(siteItems = []) {
  const ids = Array.from(
    new Set(
      siteItems
        .map((it) => it?.imovelId || it?.imovelID || it?.propertyId || it?.id)
        .filter(Boolean)
        .map(String)
    )
  );

  if (!ids.length) return siteItems;

  const imoveisRef = collection(db, 'imoveis');
  const parts = chunk(ids, 10);

  const snaps = await Promise.all(
    parts.map((part) =>
      getDocs(query(imoveisRef, where(documentId(), 'in', part)))
    )
  );

  const map = new Map();
  snaps.forEach((snap) => {
    snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
  });

  return siteItems.map((it) => {
    const pid = String(
      it?.imovelId || it?.imovelID || it?.propertyId || it?.id || ''
    );
    const full = map.get(pid);
    if (!full) return it;
    return { ...it, ...full, id: full.id };
  });
}

// ✅ modo HOME: busca direta por IDs (sem catálogo/paginação)
async function fetchImoveisByIds(ids = []) {
  const clean = Array.from(new Set(ids.filter(Boolean).map(String)));
  if (!clean.length) return [];

  const ref = collection(db, 'imoveis');
  const parts = chunk(clean, 10);

  const snaps = await Promise.all(
    parts.map((part) => getDocs(query(ref, where(documentId(), 'in', part))))
  );

  const map = new Map();
  snaps.forEach((snap) => {
    snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
  });

  // respeita ordem dos ids
  return clean.map((id) => map.get(id)).filter(Boolean);
}

// ===============================
// Query helpers (siteCatalog)
// ===============================
function buildQueryForSiteCatalog(
  ref,
  { filtros = {}, pageSize = 24, cursor = null }
) {
  const clauses = [];

  if (typeof filtros.publicado === 'boolean') {
    clauses.push(where('publicado', '==', filtros.publicado));
  }
  if (typeof filtros.destaque === 'boolean') {
    clauses.push(where('destaque', '==', filtros.destaque));
  }
  if (filtros.ref) {
    clauses.push(where('refCodigo', '==', String(filtros.ref)));
  }
  if (
    filtros.tipo &&
    !['todos', 'all'].includes(String(filtros.tipo).toLowerCase())
  ) {
    clauses.push(where('tipo', '==', fromSlugTitle(String(filtros.tipo))));
  }

  const dormMin = toNumber(filtros.dorm);
  if (!Number.isNaN(dormMin)) clauses.push(where('dormitorios', '>=', dormMin));

  const hasMin = !Number.isNaN(toNumber(filtros.min));
  const hasMax = !Number.isNaN(toNumber(filtros.max));
  if (hasMin) clauses.push(where('preco', '>=', toNumber(filtros.min)));
  if (hasMax) clauses.push(where('preco', '<=', toNumber(filtros.max)));

  const sortField = hasMin || hasMax ? 'preco' : 'createdAt';
  const sortDir = hasMin || hasMax ? 'asc' : 'desc';

  const base = [ref, ...clauses, orderBy(sortField, sortDir)];
  if (cursor) return query(...base, startAfter(cursor), limit(pageSize));
  return query(...base, limit(pageSize));
}

// ===============================
// Query helpers (fallback em imoveis)
// ===============================
function buildQueryForImoveis(
  ref,
  corretorId,
  { filtros = {}, pageSize = 24, cursor = null }
) {
  const clauses = [where('criadoPor.uid', '==', corretorId)];

  if (typeof filtros.publicado === 'boolean')
    clauses.push(where('publicado', '==', filtros.publicado));
  if (typeof filtros.destaque === 'boolean')
    clauses.push(where('destaque', '==', filtros.destaque));
  if (filtros.ref) clauses.push(where('refCodigo', '==', String(filtros.ref)));

  if (
    filtros.tipo &&
    !['todos', 'all'].includes(String(filtros.tipo).toLowerCase())
  ) {
    clauses.push(where('tipo', '==', fromSlugTitle(String(filtros.tipo))));
  }

  const dormMin = toNumber(filtros.dorm);
  if (!Number.isNaN(dormMin)) clauses.push(where('dormitorios', '>=', dormMin));

  const hasMin = !Number.isNaN(toNumber(filtros.min));
  const hasMax = !Number.isNaN(toNumber(filtros.max));
  if (hasMin) clauses.push(where('preco', '>=', toNumber(filtros.min)));
  if (hasMax) clauses.push(where('preco', '<=', toNumber(filtros.max)));

  const sortField = hasMin || hasMax ? 'preco' : 'criadoEm';
  const sortDir = hasMin || hasMax ? 'asc' : 'desc';

  const base = [ref, ...clauses, orderBy(sortField, sortDir)];
  if (cursor) return query(...base, startAfter(cursor), limit(pageSize));
  return query(...base, limit(pageSize));
}

export default function useSiteCatalog(
  corretorId,
  filtros = {},
  pageSize = 24,
  opts = {} // ✅ { mode: 'home'|'catalog', ids: [] }
) {
  const mode = opts?.mode || 'catalog';
  const ids = Array.isArray(opts?.ids) ? opts.ids : [];

  const [docs, setDocs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const [source, setSource] = useState('siteCatalog'); // 'siteCatalog' | 'imoveis' | 'ids'

  const filtrosKey = useMemo(
    () => JSON.stringify(normalizeFiltros(filtros)),
    [filtros]
  );
  const idsKey = useMemo(() => JSON.stringify(ids.map(String)), [ids]);

  // evita race: ignora resposta antiga quando muda query
  const reqIdRef = useRef(0);

  const reset = useCallback(() => {
    setDocs([]);
    setCursor(null);
    setDone(false);
    setSource('siteCatalog');
  }, []);

  useEffect(() => {
    reset();
  }, [corretorId, filtrosKey, pageSize, mode, idsKey, reset]);

  const load = useCallback(
    async ({ reset: doReset = false } = {}) => {
      if (!corretorId) return;
      if (loading) return;

      if (doReset) reset();
      if (done && !doReset) return;

      const myReq = ++reqIdRef.current;
      setLoading(true);

      try {
        const filtrosSan = normalizeFiltros(filtros);

        // ✅ HOME: se vier ids, busca direta (muito rápido)
        if (mode === 'home' && ids.length) {
          setSource('ids');
          const items = await fetchImoveisByIds(ids);
          if (reqIdRef.current !== myReq) return;
          setDocs(items);
          setCursor(null);
          setDone(true);
          return;
        }

        // ✅ CATÁLOGO: tenta siteCatalog primeiro
        if (source === 'siteCatalog') {
          const ref = collection(db, 'siteCatalog', corretorId, 'imoveis');
          const qy = buildQueryForSiteCatalog(ref, {
            filtros: filtrosSan,
            pageSize,
            cursor,
          });

          const snap = await getDocs(qy);

          // ✅ se primeira página veio vazia, cai pro fallback NA MESMA CHAMADA (sem esperar novo load)
          if (snap.empty && !cursor) {
            setSource('imoveis');

            const ref2 = collection(db, 'imoveis');
            const qy2 = buildQueryForImoveis(ref2, corretorId, {
              filtros: filtrosSan,
              pageSize,
              cursor: null,
            });

            const snap2 = await getDocs(qy2);
            if (reqIdRef.current !== myReq) return;

            if (snap2.empty) {
              setDocs([]);
              setDone(true);
              return;
            }

            const items2 = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
            setDocs(items2);
            setCursor(snap2.docs[snap2.docs.length - 1] || null);
            if (snap2.size < pageSize) setDone(true);
            return;
          }

          if (snap.empty) {
            if (reqIdRef.current !== myReq) return;
            setDone(true);
            return;
          }

          let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          // ✅ hidrata em paralelo se vier “leve”
          if (items.some((it) => !looksHydrated(it))) {
            items = await hydrateFromImoveis(items);
          }

          if (reqIdRef.current !== myReq) return;

          setDocs((prev) => [...prev, ...items]);
          setCursor(snap.docs[snap.docs.length - 1] || null);
          if (snap.size < pageSize) setDone(true);
          return;
        }

        // ✅ fallback em /imoveis (paginação)
        const ref2 = collection(db, 'imoveis');
        const qy2 = buildQueryForImoveis(ref2, corretorId, {
          filtros: filtrosSan,
          pageSize,
          cursor,
        });

        const snap2 = await getDocs(qy2);
        if (reqIdRef.current !== myReq) return;

        if (snap2.empty) {
          setDone(true);
          return;
        }

        const items2 = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDocs((prev) => [...prev, ...items2]);
        setCursor(snap2.docs[snap2.docs.length - 1] || null);
        if (snap2.size < pageSize) setDone(true);
      } catch (e) {
        console.error('[useSiteCatalog] erro ao carregar:', e?.message || e);
        setDone(true);
      } finally {
        if (reqIdRef.current === myReq) setLoading(false);
      }
    },
    [
      corretorId,
      filtros,
      pageSize,
      cursor,
      done,
      loading,
      source,
      mode,
      ids,
      reset,
    ]
  );

  return {
    docs,
    load,
    onLoadMore: load,
    loading,
    exhausted: done,
    done,
    source,
  };
}
